import ApiService from './ApiService'
import endpointConfig from '@/configs/endpoint.config'
import type {
    SignInCredential,
    SignUpCredential,
    SignInResponse,
    SignUpResponse,
    OauthCredential,
    OauthSignInResponse,
    FirebaseExchangeRequest,
    FirebaseMigrateRequest,
    RecoveryCodesResponse,
    TwoFactorSetupResponse,
    TwoFactorStatus,
    TwoFactorVerifyRequest,
    User,
} from '@/@types/auth'
import type { UserPreferences } from '@/@types/trackheros'

/**
 * Exchanges a Firebase ID token for a TrackHeros session.
 *
 * The token is a bearer of identity until it expires, so it goes in a POST body
 * and never a query string — same reasoning as `apiOauthGoogle` below, which
 * this replaces for the web.
 *
 * Throws on `403 email_not_verified`, like any other error; the caller reads
 * `code` off the body to tell that case apart from a real failure.
 */
export async function apiAuthFirebase(data: FirebaseExchangeRequest) {
    return ApiService.fetchDataWithAxios<
        OauthSignInResponse,
        FirebaseExchangeRequest
    >({
        url: endpointConfig.authFirebase,
        method: 'post',
        data,
    })
}

/**
 * Moves a pre-Firebase password into Firebase, once.
 *
 * **Answers 204 whatever happened** — success, wrong password, unknown address,
 * already migrated — so there is nothing in the response to branch on and the
 * caller must simply retry the Firebase sign-in. That is deliberate: this is an
 * unauthenticated endpoint that takes a password, and any answer beyond "done"
 * would be an oracle. See `backend/apps/accounts/views.py`.
 */
export async function apiFirebaseMigrate(data: FirebaseMigrateRequest) {
    return ApiService.fetchDataWithAxios<void, FirebaseMigrateRequest>({
        url: endpointConfig.authFirebaseMigrate,
        method: 'post',
        data,
    })
}

/** Asks the backend to mail a reset link. Always 204, never says whether the address exists. */
export async function apiRequestPasswordReset(email: string) {
    return ApiService.fetchDataWithAxios<void, { email: string }>({
        url: endpointConfig.passwordReset,
        method: 'post',
        data: { email },
    })
}

/** Asks the backend to mail a fresh confirmation link. Always 204, same reasoning. */
export async function apiResendVerification(email: string) {
    return ApiService.fetchDataWithAxios<void, { email: string }>({
        url: endpointConfig.verifyEmailResend,
        method: 'post',
        data: { email },
    })
}

/**
 * Tells the backend a password was just reset, so it can mail the notice.
 *
 * Takes an ID token rather than an address, unlike the two above. Those mail a
 * one-time link to whoever owns the mailbox, which is harmless whoever asked;
 * this one mails a statement *about the account*, and a bare address would let
 * anyone tell any user their password had just changed.
 */
export async function apiNotifyPasswordChanged(idToken: string) {
    return ApiService.fetchDataWithAxios<void, { idToken: string }>({
        url: endpointConfig.passwordChanged,
        method: 'post',
        data: { idToken },
    })
}

/**
 * The second half of a sign-in for an account with two-factor on: the
 * challenge from the 403, plus a six-digit code or a recovery code. Answers
 * exactly what a sign-in answers.
 *
 * Refusals are 400 (`two_factor_invalid` / `two_factor_expired`) or 429
 * (`two_factor_locked`) — never 401, which the response interceptor would read
 * as a dead session.
 */
export async function apiTwoFactorVerify(data: TwoFactorVerifyRequest) {
    return ApiService.fetchDataWithAxios<
        OauthSignInResponse,
        TwoFactorVerifyRequest
    >({
        url: endpointConfig.twoFactorVerify,
        method: 'post',
        data,
    })
}

export async function apiGetTwoFactor() {
    return ApiService.fetchDataWithAxios<TwoFactorStatus>({
        url: endpointConfig.twoFactor,
        method: 'get',
    })
}

/** A fresh secret to scan. Replaces an unfinished setup; 409 once it is on. */
export async function apiTwoFactorSetup() {
    return ApiService.fetchDataWithAxios<TwoFactorSetupResponse>({
        url: endpointConfig.twoFactorSetup,
        method: 'post',
    })
}

/** The first code from the app turns it on. The recovery codes come back once. */
export async function apiTwoFactorEnable(code: string) {
    return ApiService.fetchDataWithAxios<
        RecoveryCodesResponse,
        { code: string }
    >({
        url: endpointConfig.twoFactorEnable,
        method: 'post',
        data: { code },
    })
}

/** Needs a current code or a recovery code — a session alone is not enough. */
export async function apiTwoFactorDisable(code: string) {
    return ApiService.fetchDataWithAxios<void, { code: string }>({
        url: endpointConfig.twoFactorDisable,
        method: 'post',
        data: { code },
    })
}

/** Replaces every recovery code. Needs a current code, like turning it off. */
export async function apiTwoFactorRecoveryCodes(code: string) {
    return ApiService.fetchDataWithAxios<
        RecoveryCodesResponse,
        { code: string }
    >({
        url: endpointConfig.twoFactorRecoveryCodes,
        method: 'post',
        data: { code },
    })
}

/**
 * Ends every session on every device, this one included. The caller signs out
 * locally afterwards — this browser's refresh token is dead too.
 */
export async function apiRevokeAllSessions() {
    return ApiService.fetchDataWithAxios<void>({
        url: endpointConfig.sessionsRevokeAll,
        method: 'post',
    })
}

/** @deprecated Pre-Firebase. Still answering for old mobile builds; the SPA does not call it. */
export async function apiSignIn(data: SignInCredential) {
    return ApiService.fetchDataWithAxios<SignInResponse>({
        url: endpointConfig.signIn,
        method: 'post',
        data,
    })
}

/** @deprecated Pre-Firebase. See `apiSignIn`. */
export async function apiSignUp(data: SignUpCredential) {
    return ApiService.fetchDataWithAxios<SignUpResponse>({
        url: endpointConfig.signUp,
        method: 'post',
        data,
    })
}

/**
 * Exchanges a Google ID token for a TrackHeros session.
 *
 * The credential is worthless to anyone who intercepts it after the fact — the
 * backend checks its audience and expiry — but it is still a bearer of
 * identity, so it goes in a POST body and never a query string.
 *
 * @deprecated Pre-Firebase. Google now goes through `apiAuthFirebase`; this
 * stays only until the mobile builds that call it are gone.
 */
export async function apiOauthGoogle(credential: string) {
    return ApiService.fetchDataWithAxios<OauthSignInResponse, OauthCredential>({
        url: endpointConfig.oauthGoogle,
        method: 'post',
        data: { credential },
    })
}

/**
 * Blacklists the refresh token server-side. The access token stays valid until
 * it expires — that is inherent to stateless JWTs — so the caller must clear
 * local state regardless of whether this request succeeds.
 */
export async function apiSignOut(refreshToken: string) {
    return ApiService.fetchDataWithAxios({
        url: endpointConfig.signOut,
        method: 'post',
        data: { refreshToken },
    })
}

export async function apiGetCurrentUser() {
    return ApiService.fetchDataWithAxios<{ user: User }>({
        url: endpointConfig.me,
        method: 'get',
    })
}

export async function apiGetPreferences() {
    return ApiService.fetchDataWithAxios<UserPreferences>({
        url: endpointConfig.preferences,
        method: 'get',
    })
}

/**
 * `suggested_names` is excluded because the server computes it from the account
 * name on every read. Sending it would be a write the API correctly ignores,
 * which is the kind of thing that later reads as a bug.
 */
export type UserPreferencesPatch = Partial<
    Omit<
        UserPreferences,
        | 'suggested_names'
        | 'meeting_max_minutes_ceiling'
        | 'meeting_min_minutes'
        | 'meeting_end_reminder_lead_minutes'
    >
>

export async function apiUpdatePreferences(data: UserPreferencesPatch) {
    return ApiService.fetchDataWithAxios<UserPreferences, UserPreferencesPatch>(
        {
            url: endpointConfig.preferences,
            method: 'patch',
            data,
        },
    )
}
